// Dev fallback must match the project's API Gateway port (7002, per CLAUDE.md).
// Was ':3000' — wrong, so the portal only worked when VITE_API_URL was set.
export const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:7002'
export const TOKEN_KEY = 'bainner_token'

// WebSocket primary vs HTTP-polling fallback (serverless deploy). Local dev and
// the ECS stack keep WS (default true); AWS Lambda builds have no WS endpoint and
// set `VITE_WS_ENABLED=false`, which switches every live-progress consumer to
// polling the same HTTP status endpoints they already use for their one-shot
// fallback. Read once at module load — a build-time constant.
export const WS_ENABLED = (import.meta.env.VITE_WS_ENABLED ?? 'true') !== 'false'

import type { LoopEffort, LoopModelKey, PaginatedResponse } from '@bainner/shared'
import type {
  Banner,
  Brand,
  BrandListItem,
  BrandListParams,
  Campaign,
  CampaignFont,
  CampaignListItem,
  CampaignListParams,
  CreateCampaignInput,
  CreateVariantInput,
  DimensionStatusResponse,
  ExportStatusResponse,
  GenerateDimensionsResponse,
  MasterBanner,
  MasterExportTriggerResponse,
  RegenerateDimensionInput,
  RegenerateDimensionResponse,
  ComposePreviewInput,
  ComposePreviewResponse,
  ReshapeDimensionInput,
  ReshapeDimensionResponse,
  RequiredFont,
  RatioThresholdsOverride,
  DimensionSourceEntry,
  DimensionSourcesResponse,
  SourceSelectionEntry,
  SourceSelectionsResponse,
  EnsureSourceSelectionsResponse,
  Spec,
  SpecInput,
  Variant,
  VariantCostsResponse,
  ImageProviderHealth,
  ImageProviderRetryResult,
  DimensionJob,
} from './api-types.js'

export class ApiError extends Error {
  constructor(
    public statusCode: number,
    message: string,
    public details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem(TOKEN_KEY)
  const headers: Record<string, string> = {
    ...((options.headers as Record<string, string>) ?? {}),
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  // Only declare a JSON body when one is actually sent. A bodyless request (GET,
  // DELETE) with `Content-Type: application/json` makes Fastify reject it with
  // "Body cannot be empty when content-type is set to 'application/json'".
  if (options.body !== undefined && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json'
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  })

  if (response.status === 401) {
    localStorage.removeItem(TOKEN_KEY)
    window.location.href = '/login'
    throw new ApiError(401, 'Unauthorized')
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({}))
    throw new ApiError(
      response.status,
      (body as { message?: string }).message ?? response.statusText,
      body,
    )
  }

  if (response.status === 204) {
    return undefined as T
  }

  return response.json() as Promise<T>
}

/** Builds a `?a=1&b=2` query string from defined params only, `''` when none are set. */
function buildQuery(params: Record<string, string | number | undefined>): string {
  const qs = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) qs.set(key, String(value))
  }
  const query = qs.toString()
  return query ? `?${query}` : ''
}

export const apiClient = {
  get<T>(path: string): Promise<T> {
    return request<T>(path)
  },

  post<T>(path: string, body: unknown): Promise<T> {
    return request<T>(path, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },

  postMultipart<T>(path: string, formData: FormData): Promise<T> {
    return request<T>(path, {
      method: 'POST',
      body: formData,
    })
  },

  put<T>(path: string, body: unknown): Promise<T> {
    return request<T>(path, {
      method: 'PUT',
      body: JSON.stringify(body),
    })
  },

  delete<T>(path: string): Promise<T> {
    return request<T>(path, { method: 'DELETE' })
  },

  /** List a campaign's registered font faces (P00 route → P02 FontFace set). */
  getCampaignFonts(campaignId: string): Promise<CampaignFont[]> {
    return request<CampaignFont[]>(`/campaigns/${campaignId}/fonts`)
  },

  /**
   * The campaign's imported MASTER banners (design sources) — for the Export
   * screen's separate master section. Delivery/spec-export subtract these; this
   * read returns them on purpose so the agency can preview + download the source.
   */
  getMasterBanners(campaignId: string): Promise<{ banners: MasterBanner[] }> {
    return request<{ banners: MasterBanner[] }>(`/campaigns/${campaignId}/master-banners`)
  },

  /** Trigger a masters-only ZIP (own `master/` folder tree, no spec). */
  triggerMasterExport(campaignId: string): Promise<MasterExportTriggerResponse> {
    return request<MasterExportTriggerResponse>(`/campaigns/${campaignId}/master-export`, {
      method: 'POST',
    })
  },

  getMasterExportStatus(campaignId: string, jobId: string): Promise<ExportStatusResponse> {
    return request<ExportStatusResponse>(
      `/campaigns/${campaignId}/master-export/status?jobId=${encodeURIComponent(jobId)}`,
    )
  },

  /** Trigger a combined ALL-SPECS ZIP (one ZIP, a folder per spec). */
  triggerAllSpecsExport(campaignId: string): Promise<MasterExportTriggerResponse> {
    return request<MasterExportTriggerResponse>(`/campaigns/${campaignId}/export-all`, {
      method: 'POST',
    })
  },

  getAllSpecsExportStatus(campaignId: string, jobId: string): Promise<ExportStatusResponse> {
    return request<ExportStatusResponse>(
      `/campaigns/${campaignId}/export-all/status?jobId=${encodeURIComponent(jobId)}`,
    )
  },

  /** The design's (family, weight) set with a `registered` flag (P03 panel). */
  getRequiredFonts(campaignId: string): Promise<RequiredFont[]> {
    return request<RequiredFont[]>(`/campaigns/${campaignId}/fonts/required`)
  },

  /**
   * Upload one real font file mapped to an exact (family, weight) — mirrors the
   * image-import multipart flow. Family/weight are pre-filled from the required
   * row so the bytes resolve against `resolveFontFaces`' exact lookup. Returns
   * the created/updated row.
   */
  /**
   * Upload ONE image and get back where it now lives. The caller writes the returned `s3Key` into
   * an element's `asset` and PUTs the definition — this call stores bytes, nothing more.
   *
   * `s3Key` is the field that matters: the gateway rebuilds every asset `url` from it on each
   * read, so a persisted `url` is a convenience for an immediate optimistic render, never the
   * source of truth for which picture ships.
   */
  uploadBannerAsset(
    campaignId: string,
    variantId: string,
    file: File,
  ): Promise<{ s3Key: string; url: string; mimeType: string; originalFilename?: string }> {
    const form = new FormData()
    form.append('file', file)
    return request(`/campaigns/${campaignId}/variants/${variantId}/assets`, {
      method: 'POST',
      body: form,
    })
  },

  uploadFont(
    campaignId: string,
    file: File,
    meta: { family: string; weight: number; style?: string },
  ): Promise<CampaignFont> {
    const form = new FormData()
    form.append('fontFamily', meta.family)
    form.append('fontWeight', String(meta.weight))
    if (meta.style) form.append('fontStyle', meta.style)
    form.append('file', file)
    return request<CampaignFont>(`/campaigns/${campaignId}/fonts`, {
      method: 'POST',
      body: form,
    })
  },

  /**
   * Remove one uploaded face by id (recovery for a wrong upload). After this the
   * face shows as missing again so the correct file can be re-uploaded. Returns
   * 204 with no body.
   */
  deleteFont(campaignId: string, fontId: string): Promise<void> {
    return request<undefined>(`/campaigns/${campaignId}/fonts/${fontId}`, { method: 'DELETE' })
  },

  /**
   * Create a campaign (Sprint 028 — the SPA "New campaign" flow). Returns the
   * created row; the caller follows up with `createVariant` to seed the master.
   *
   * `input.id`, optional (campaign-id-before-create fix — see `CreateCampaignInput` in
   * `api-types.ts`): when a caller sends it, the created row gets THAT id instead of a
   * server-minted one — the server 409s rather than update/upsert if it already names an
   * existing campaign. Omitting it keeps today's behavior byte-for-byte.
   */
  createCampaign(input: CreateCampaignInput): Promise<Campaign> {
    return request<Campaign>('/campaigns', {
      method: 'POST',
      body: JSON.stringify(input),
    })
  },

  /**
   * Delete a campaign and EVERYTHING under it — its variants + their banners
   * (FK cascade), media-agency specs, exports, and the campaign's entire S3
   * asset tree (`campaigns/<id>/`, swept server-side). Irreversible. Returns
   * 204 with no body.
   */
  async deleteCampaign(campaignId: string): Promise<void> {
    // 204 — `request` resolves undefined; the generic just can't be `void`.
    await request<undefined>(`/campaigns/${campaignId}`, { method: 'DELETE' })
  },

  /**
   * Create a variant under a campaign. In the creation flow this is called with
   * `{ name: 'Master', isMaster: true }` — the variant the Figma plugin needs.
   * Multi-master (Epic 31): an additional master design gets its own
   * `isMaster: true` variant the same way. May 200 instead of 201 (reuse-first:
   * the gateway hands back an existing EMPTY master) — `request()` treats every
   * 2xx alike; callers must not branch on the status code.
   */
  createVariant(campaignId: string, input: CreateVariantInput): Promise<Variant> {
    return request<Variant>(`/campaigns/${campaignId}/variants`, {
      method: 'POST',
      body: JSON.stringify(input),
    })
  },

  /**
   * Delete one master variant and everything under it — its banners (FK cascade)
   * and its S3 asset tree (imported design PNGs + every generated dimension).
   * Irreversible. The DEFAULT master is derived (earliest-created), so deleting
   * the current default simply hands the role to the next-oldest master.
   */
  /**
   * Delete ONE generated banner. The route has existed and been agency-authorized all along
   * (`routes/banners/index.ts`'s `DELETE /:bannerId`); it simply had no frontend caller until the
   * workspace's per-size kebab needed one (2026-08-31).
   *
   * NOT the way to remove a MASTER. A master's row is what `findMasterBannerId` and every
   * `masterBannerIds` reader key off; deleting it here would leave the variant alive with no
   * banner and corrupt that bookkeeping. `deleteVariant` below is the master's own path, and it
   * cascades the whole variant on purpose.
   */
  async deleteBanner(campaignId: string, variantId: string, bannerId: string): Promise<void> {
    // 204 — `request` resolves undefined; the generic just can't be `void`.
    await request<undefined>(`/campaigns/${campaignId}/variants/${variantId}/banners/${bannerId}`, {
      method: 'DELETE',
    })
  },

  async deleteVariant(campaignId: string, variantId: string): Promise<void> {
    // 204 — `request` resolves undefined; the generic just can't be `void`.
    await request<undefined>(`/campaigns/${campaignId}/variants/${variantId}`, { method: 'DELETE' })
  },

  /**
   * List a campaign's variants (Sprint 021 Variants tab), ordered by sortOrder
   * server-side. Paginated; a campaign has few variants, so fetch the max page
   * size (100) and return the rows directly. `media_agency` is excluded from
   * this route server-side by design.
   *
   * ⚠ Multi-master (Epic 31): `sortOrder` is user-mutable — NEVER resolve the
   * default master from this list's position; use `lib/master-variant.ts`
   * (earliest-created master, the backend's identity axis).
   */
  async getVariants(campaignId: string): Promise<Variant[]> {
    const res = await request<{ data: Variant[] }>(`/campaigns/${campaignId}/variants?pageSize=100`)
    return res.data
  },

  /**
   * List a variant's banners (Sprint 021 Banners tab), ordered by createdAt
   * server-side. Paginated; fetch the max page size (100) — a variant has a
   * master plus a bounded set of generated dimensions.
   *
   * `specId` scopes the list to the MASTER + that spec's own generated banners, so a
   * newly-created spec starts clean instead of matching other specs' banners by (w,h).
   *
   * REQUIRED, and `null` means "the whole variant, deliberately" (2026-07-25). It used
   * to be optional, and forgetting it was silent and wrong rather than a type error —
   * three separate bugs in one day came from exactly that: a batch announced complete
   * within seconds because sibling specs already held its sizes, a re-roll's "prior"
   * map picked up another spec's banner ids, and a fresh spec's tile displayed an older
   * spec's cost. Every call site now has to state which roster it means.
   */
  async getBanners(
    campaignId: string,
    variantId: string,
    specId: string | null,
  ): Promise<Banner[]> {
    const specQuery = specId ? `&specId=${encodeURIComponent(specId)}` : ''
    const res = await request<{ data: Banner[] }>(
      `/campaigns/${campaignId}/variants/${variantId}/banners?pageSize=100${specQuery}`,
    )
    return res.data
  },

  /**
   * Flip the HUMAN ship-approval on a banner (2026-07-21 UAT). `approved: true` ships a
   * `blocked`/`degraded` banner the user looked at and accepts (the AI verdict stays on the
   * row); `false` reverts to the AI gate. Returns the updated banner.
   */
  approveBanner(
    campaignId: string,
    variantId: string,
    bannerId: string,
    approved: boolean,
  ): Promise<Banner> {
    return request<Banner>(
      `/campaigns/${campaignId}/variants/${variantId}/banners/${bannerId}/approve`,
      { method: 'PATCH', body: JSON.stringify({ approved }) },
    )
  },

  /**
   * Move generated banners to another spec (spec-scoped-banners Task 6 — the SpecsTab
   * "Üretilenleri taşı" flow). A REAL move: the banners' `spec_id` is reassigned server-side,
   * so they show under the target spec and drop from the source (their bytes never move).
   * `bannerIds` must all belong to `variantId`; `targetSpecId` a spec on the campaign. A
   * (variant, spec, size) uniqueness clash 409s (the target already owns that size).
   */
  moveBannersToSpec(
    campaignId: string,
    variantId: string,
    input: { bannerIds: string[]; targetSpecId: string },
  ): Promise<{ moved: string[] }> {
    return request<{ moved: string[] }>(
      `/campaigns/${campaignId}/variants/${variantId}/banners/move-spec`,
      { method: 'PATCH', body: JSON.stringify(input) },
    )
  },

  /**
   * A variant's recorded per-generation AI costs, newest first (Sprint 051). One
   * fetch backs every card's latest-cost chip (newest row per width×height) and the
   * edit drawer's date-ordered history table (a dimension's rows). Agency-only.
   */
  /**
   * Is image generation usable? Free to ask (the ai-gateway remembers the last
   * refusal rather than probing), and it never fails — an unreachable gateway
   * answers `unknown`, which the UI treats as "no claim".
   */
  async getImageProviderHealth(): Promise<ImageProviderHealth> {
    return request<ImageProviderHealth>('/reports/image-provider')
  },

  /**
   * "I raised the limit — try again" (2026-07-27). Forgets a recorded refusal on both
   * halves (the durable row and the ai-gateway's own memory) and answers with the state
   * that results. Nothing re-opens the gate on a timer, because finding out whether an
   * account is still refused costs a paid dimension — so this is the way back.
   */
  async resetImageProviderBlock(): Promise<ImageProviderRetryResult> {
    return request<ImageProviderRetryResult>('/reports/image-provider/reset', { method: 'POST' })
  },

  async getVariantCosts(
    variantId: string,
    // REQUIRED but nullable — same mold as `getBanners`. Forgetting the scope used to
    // fall back to the whole variant, so a brand-new spec's tile displayed an OLDER
    // spec's spend for the same size. `null` means "every spec", written on purpose.
    specId: string | null,
  ): Promise<VariantCostsResponse> {
    // Scope to the spec when the caller has one: the ledger is keyed by
    // (variant, W×H) with no spec of its own, so an unscoped read lets one spec's
    // tile display a sibling spec's spend for the same size (2026-07-25).
    const qs = specId ? `?specId=${encodeURIComponent(specId)}` : ''
    return request<VariantCostsResponse>(`/reports/variant/${variantId}/costs${qs}`)
  },

  /**
   * List ALL of a variant's banners, paging until every row is read. `pageSize` is
   * server-clamped to 100, so a variant with more than 100 banners is TRUNCATED by
   * `getBanners` — any consumer that must reason about every banner (e.g. the Export
   * tab's blocked verdict) has to page. Uses the response's `totalPages`.
   */
  async getAllBanners(campaignId: string, variantId: string): Promise<Banner[]> {
    const base = `/campaigns/${campaignId}/variants/${variantId}/banners?pageSize=100`
    const first = await request<{ data: Banner[]; totalPages?: number }>(`${base}&page=1`)
    const rows = [...first.data]
    const totalPages = first.totalPages ?? 1
    for (let page = 2; page <= totalPages; page++) {
      const next = await request<{ data: Banner[] }>(`${base}&page=${page}`)
      rows.push(...next.data)
    }
    return rows
  },

  /**
   * Presigned download URL for the packed Figma plugin ZIP (Epic 31 Phase 10 —
   * the tutorial lightbox's "Plugini İndir"). 404 when the ZIP has not been
   * published to this environment's S3 bucket yet.
   */
  getPluginDownloadUrl(): Promise<{ url: string; filename: string }> {
    return request<{ url: string; filename: string }>('/figma-plugin/download')
  },

  /**
   * The campaign's pinned per-ratio-group adaptation-source picks (Epic 31
   * Phase 08 — the source-master picker's read): AI choice + reasoning +
   * degraded/overridden/stale flags, plus the master count that gates the
   * picker's rendering.
   */
  getSourceSelections(campaignId: string): Promise<SourceSelectionsResponse> {
    return request<SourceSelectionsResponse>(`/campaigns/${campaignId}/source-selections`)
  },

  /**
   * Pre-warm AI source picks for every ratio group without a current-set pin
   * (picker auto-default). Long-running: each missing group runs a real AI
   * selection. Returns the refreshed GET shape plus transiently-failed groups.
   */
  ensureSourceSelections(campaignId: string): Promise<EnsureSourceSelectionsResponse> {
    return request<EnsureSourceSelectionsResponse>(
      `/campaigns/${campaignId}/source-selections/ensure`,
      { method: 'POST' },
    )
  },

  /**
   * Persist a manual source override for one ratio group (Epic 31 Phase 08).
   * The override is pinned under the CURRENT master-set signature and WINS over
   * the AI pick on every subsequent generation (including "Farklı versiyon").
   */
  overrideSourceSelection(
    campaignId: string,
    ratioGroup: 'horizontal' | 'square' | 'vertical',
    sourceVariantId: string,
  ): Promise<SourceSelectionEntry> {
    return request<SourceSelectionEntry>(
      `/campaigns/${campaignId}/source-selections/${ratioGroup}`,
      { method: 'PUT', body: JSON.stringify({ sourceVariantId }) },
    )
  },

  /**
   * The effective per-DIMENSION source + provenance for every dimension on a
   * spec (Epic 43 Phase 06, task 06.4/06.5 — the dimension-source picker's
   * read + the produced-tile provenance line), finer-grained than
   * `getSourceSelections` (per RATIO GROUP). This phase STORES and SHOWS
   * only — nothing reads it as a generation input yet (Phase 07). Matches
   * `services/api-gateway/src/routes/variants/dimension-source.ts` exactly.
   */
  getDimensionSources(
    campaignId: string,
    variantId: string,
    specId: string,
  ): Promise<DimensionSourcesResponse> {
    return request<DimensionSourcesResponse>(
      `/campaigns/${campaignId}/variants/${variantId}/dimension-source?specId=${encodeURIComponent(specId)}`,
    )
  },

  /**
   * Persist (or clear, via `sourceVariantId: null`) a manual per-dimension
   * source override. `authorize(['agency'])` server-side; a `sourceVariantId`
   * from another campaign's master 404s. Returns the ONE affected dimension's
   * fresh entry — the caller merges it into its own `getDimensionSources` map
   * rather than refetching the whole list.
   */
  setDimensionSource(
    campaignId: string,
    variantId: string,
    specId: string,
    width: number,
    height: number,
    sourceVariantId: string | null,
  ): Promise<DimensionSourceEntry> {
    return request<DimensionSourceEntry>(
      `/campaigns/${campaignId}/variants/${variantId}/dimension-source`,
      { method: 'PUT', body: JSON.stringify({ specId, width, height, sourceVariantId }) },
    )
  },

  /**
   * Clear a per-dimension source override — a thin `sourceVariantId: null`
   * convenience wrapper over `setDimensionSource` (the route has no separate
   * DELETE). The dimension falls back to its ratio group's pick on its next
   * generation (Phase 07; this phase does not yet read it).
   */
  clearDimensionSource(
    campaignId: string,
    variantId: string,
    specId: string,
    width: number,
    height: number,
  ): Promise<DimensionSourceEntry> {
    return request<DimensionSourceEntry>(
      `/campaigns/${campaignId}/variants/${variantId}/dimension-source`,
      { method: 'PUT', body: JSON.stringify({ specId, width, height, sourceVariantId: null }) },
    )
  },

  /**
   * The campaign's horizontal/square/vertical ratio-group boundary override
   * (Epic 43 Phase 04 — the ratio-threshold panel's read). `null` means no
   * override is set; the campaign falls back to `DEFAULT_RATIO_THRESHOLDS`.
   */
  getRatioThresholds(campaignId: string): Promise<RatioThresholdsOverride | null> {
    return request<RatioThresholdsOverride | null>(`/campaigns/${campaignId}/ratio-thresholds`)
  },

  /**
   * Rename a campaign, edit its description, or move it between draft/active/archived.
   *
   * ADDED 2026-08-31, and the point is what it replaces. `PUT /campaigns/:campaignId` has existed
   * and worked all along (`services/api-gateway/src/routes/campaigns/index.ts:69`, agency-guarded,
   * with a real drizzle update behind it) — this file simply never wrapped it. Because this file
   * is the SPA's only sanctioned way to reach the gateway, every author who went looking concluded
   * the capability did not exist and designed around it: four separate places in `apps/web` state
   * in prose that "no rename endpoint exists", and one of them downgraded a field the Figma draws
   * as editable into read-only text on that basis. A missing three-line wrapper became a missing
   * feature, and then became documented fact. Those comments are corrected alongside this.
   *
   * Every field is optional and only the ones passed are written — the handler spreads
   * conditionally, so omitting `description` does not null it.
   */
  updateCampaign(
    campaignId: string,
    input: { name?: string; description?: string | null; status?: 'draft' | 'active' | 'archived' },
  ): Promise<Campaign> {
    return request<Campaign>(`/campaigns/${campaignId}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    })
  },

  /**
   * Persist a campaign-scoped ratio-group boundary override. The route itself
   * rejects `vertical >= horizontal` (Epic 43 Phase 04 task 04.2) — the
   * panel's own inverted-band guard is a courtesy, not the guarantee.
   */
  updateRatioThresholds(
    campaignId: string,
    thresholds: RatioThresholdsOverride,
  ): Promise<RatioThresholdsOverride | null> {
    return request<RatioThresholdsOverride | null>(`/campaigns/${campaignId}/ratio-thresholds`, {
      method: 'PUT',
      body: JSON.stringify(thresholds),
    })
  },

  /**
   * Clear the campaign's ratio-group boundary override — the campaign falls
   * back to `DEFAULT_RATIO_THRESHOLDS` on its next generation.
   */
  deleteRatioThresholds(campaignId: string): Promise<RatioThresholdsOverride | null> {
    return request<RatioThresholdsOverride | null>(`/campaigns/${campaignId}/ratio-thresholds`, {
      method: 'DELETE',
    })
  },

  /**
   * Kick off AI dimension generation for a spec's sizes off the master variant
   * (Epic 12 — the "Generate" action in the Specs tab). Async: the gateway 202s
   * with a jobId and the generator processes via RabbitMQ; the caller polls
   * `getBanners` until the spec's dimensions appear. `overwrite` re-runs over any
   * existing dimensions (a non-overwrite re-run 409s). `forceFresh` (Sprint 039 —
   * "Farklı versiyon dene") makes the re-run skip the layout/plan cache so the AI
   * produces a genuinely new composition instead of replaying the pinned one;
   * pair it with `overwrite` since it only applies to existing dimensions.
   * `repair` (Epic 27 — "Düzelterek üret") makes the run seed each dimension's
   * prior output and correct deviations vs the master instead of composing fresh;
   * only meaningful with `engine: 'gpt-image'`.
   */
  generateDimensions(
    campaignId: string,
    variantId: string,
    specId: string,
    overwrite = false,
    forceFresh = false,
    engine?: 'vision' | 'gpt-image',
    repair?: boolean,
    // Sprint 045 item 1 — the user's öncelik list (master element ids, highest
    // priority first); one shared order for the whole batch run.
    priorityOrder?: string[],
    // Epic 33 Phase 01 — per-run loop economics (UI inputs land in Phase 03):
    // quality target the loop iterates toward + per-dimension spend ceiling.
    scoreTarget?: number,
    maxCostUsd?: number,
    // Agency-selected protected element ids (master element ids the agency wants
    // shielded from the vision loop's lowest-first content reduction, on top of
    // the AI-derived core role). Mirrors `priorityOrder`: per-run agency intent,
    // sent only when non-empty; absent ⇒ byte-identical role-only behavior.
    protectedElementIds?: string[],
    // Epic 36 — per-run loop MODEL + reasoning effort (quality-mode presets).
    // Appended last (matching the protectedElementIds precedent) so existing
    // positional callers stay valid. WIRE KEYS, never Bedrock ids: `model ∈
    // LOOP_MODEL_KEYS`, `effort ∈ LOOP_EFFORTS`; the ai-gateway resolves a key →
    // id. Sent per the preset send-mold; absent ⇒ backend config default
    // (`config.VISION_LOOP_MODEL` / `VISION_LOOP_EFFORT`), byte-identical to
    // today's no-model run (Epic 36 SC #7).
    model?: LoopModelKey,
    effort?: LoopEffort,
  ): Promise<GenerateDimensionsResponse> {
    return request<GenerateDimensionsResponse>(
      `/campaigns/${campaignId}/variants/${variantId}/generate-dimensions`,
      {
        method: 'POST',
        body: JSON.stringify({
          specId,
          overwrite,
          forceFresh,
          ...(engine && { engine }),
          ...(repair && { repair }),
          ...(priorityOrder && priorityOrder.length > 0 && { priorityOrder }),
          ...(scoreTarget !== undefined && { scoreTarget }),
          ...(maxCostUsd !== undefined && { maxCostUsd }),
          ...(model !== undefined && { model }),
          ...(effort !== undefined && { effort }),
          ...(protectedElementIds && protectedElementIds.length > 0 && { protectedElementIds }),
        }),
      },
    )
  },

  /**
   * Stop a running batch generate (2026-07-20 UAT — the Durdur button). Records a cancel
   * flag keyed by the run's `jobId`; the generator stops producing further sizes after the
   * in-flight one finishes. 202 `{ status: 'cancelling' }`. Idempotent — a repeat is a no-op.
   */
  cancelGenerate(
    campaignId: string,
    variantId: string,
    jobId: string,
  ): Promise<{ status: 'cancelling' }> {
    return request<{ status: 'cancelling' }>(
      `/campaigns/${campaignId}/variants/${variantId}/generate-dimensions/cancel`,
      { method: 'POST', body: JSON.stringify({ jobId }) },
    )
  },

  /**
   * Prompt-guided regenerate of ONE generated dimension off the master variant
   * (Epic 17 Phase 05/06). 202s with a jobId; the generator re-rolls the
   * dimension fresh (vision loop, minutes-scale) and REPLACES its banner row
   * (new bannerId) — the caller polls `getBanners` until the dimension's id
   * changes. Notable starts-rejections: 409 when the dimension was never
   * generated ("run generate-dimensions first"), 400 on a non-master variant.
   */
  regenerateDimension(
    campaignId: string,
    variantId: string,
    input: RegenerateDimensionInput,
  ): Promise<RegenerateDimensionResponse> {
    return request<RegenerateDimensionResponse>(
      `/campaigns/${campaignId}/variants/${variantId}/regenerate-dimension`,
      { method: 'POST', body: JSON.stringify(input) },
    )
  },

  /**
   * Image-first COMPOSE TARGET preview (Epic 26). Synchronously returns the
   * photo-led composed target image (base64) for a dimension so the UI can show it
   * before the user runs an image-first regenerate. Read-only.
   */
  composePreview(
    campaignId: string,
    variantId: string,
    input: ComposePreviewInput,
  ): Promise<ComposePreviewResponse> {
    return request<ComposePreviewResponse>(
      `/campaigns/${campaignId}/variants/${variantId}/compose-preview`,
      { method: 'POST', body: JSON.stringify(input) },
    )
  },

  /**
   * HTTP fallback (Sprint 032 Item D/E) for the `dimension:{campaignId}:{jobId}`
   * WS channel — read once on subscribe in case the socket can't connect or the
   * replay was missed. Returns a neutral `processing` sentinel before the first
   * worker event lands, and a synthetic `TIMEOUT` failure for a dead job.
   */
  getDimensionStatus(
    campaignId: string,
    variantId: string,
    jobId: string,
  ): Promise<DimensionStatusResponse> {
    return request<DimensionStatusResponse>(
      `/campaigns/${campaignId}/variants/${variantId}/regenerate-dimension/status?jobId=${encodeURIComponent(jobId)}`,
    )
  },

  /**
   * Prompt-driven reshape (Epic 18 Phase 03): create ONE new size off the master
   * variant. The gateway appends `{width,height}` to the spec (so it becomes a
   * first-class, deliverable dimension) and single-target generates just that
   * banner; 202s with a jobId. The caller polls `getBanners` until a banner at
   * the new size appears. Start-rejections: 422 (duplicates the master size), 409
   * (already on the spec / already generated), 400 (non-master variant).
   */
  reshapeDimension(
    campaignId: string,
    variantId: string,
    input: ReshapeDimensionInput,
  ): Promise<ReshapeDimensionResponse> {
    return request<ReshapeDimensionResponse>(
      `/campaigns/${campaignId}/variants/${variantId}/reshape-dimension`,
      { method: 'POST', body: JSON.stringify(input) },
    )
  },

  /**
   * List a campaign's media-agency specs (Sprint 017 Specs tab). The endpoint is
   * paginated; a campaign has at most a handful of specs, so we fetch the max
   * page size (100) and return the rows directly.
   */
  /** Dimension jobs of the last `sinceHours` (default 24) — see `DimensionJob`. */
  async getDimensionJobs(campaignId: string, sinceHours = 24): Promise<DimensionJob[]> {
    const res = await request<{ jobs: DimensionJob[] }>(
      `/campaigns/${campaignId}/dimension-jobs?sinceHours=${String(sinceHours)}`,
    )
    return res.jobs
  },

  async getSpecs(campaignId: string): Promise<Spec[]> {
    const res = await request<{ data: Spec[] }>(`/campaigns/${campaignId}/specs?pageSize=100`)
    return res.data
  },

  /**
   * Create a media-agency spec (Sprint 020). Body mirrors the route's zod rules
   * (`agencyName` 1–255, `dimensions` ≥1 with positive ints); the form validates
   * client-side first so a 400 here is unexpected.
   */
  createSpec(campaignId: string, input: SpecInput): Promise<Spec> {
    return request<Spec>(`/campaigns/${campaignId}/specs`, {
      method: 'POST',
      body: JSON.stringify(input),
    })
  },

  /** Replace a spec's editable fields (agencyName + dimensions). PUT. */
  updateSpec(campaignId: string, specId: string, input: SpecInput): Promise<Spec> {
    return request<Spec>(`/campaigns/${campaignId}/specs/${specId}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    })
  },

  /** Delete a spec. Returns 204 with no body. */
  deleteSpec(campaignId: string, specId: string): Promise<void> {
    return request<undefined>(`/campaigns/${campaignId}/specs/${specId}`, { method: 'DELETE' })
  },

  /**
   * Mint a fresh 7-day media-agency delivery token scoped to (campaign, spec) —
   * the bearer embedded in a `/deliver/:token` share link. Each call returns a
   * new token (no revocation yet — that's the delivery-link-lifecycle sprint).
   */
  async mintMediaAgencyDeliveryToken(campaignId: string, specId: string): Promise<string> {
    const res = await request<{ token: string }>('/auth/token/media-agency', {
      method: 'POST',
      body: JSON.stringify({ campaignId, specId }),
    })
    return res.token
  },

  // ── Brands & per-user favourites (Epic 48 Phase 00B, API-BRIEF.md) ───────

  /**
   * List every brand, agency-wide — Brand is a campaign folder, not a
   * tenant, so there is no per-brand access boundary to filter by (D-BRAND-1).
   * Each row carries the calling user's own favourite state. Backs the
   * page-header brand switcher / brand-scoping context (wiring lands in
   * Phase 11 — this is the data path only).
   */
  getBrands(params?: BrandListParams): Promise<PaginatedResponse<BrandListItem>> {
    return request<PaginatedResponse<BrandListItem>>(
      `/brands${buildQuery({ page: params?.page, pageSize: params?.pageSize })}`,
    )
  },

  /**
   * One brand's record — backs the Import page's brand breadcrumb and any
   * other single-brand context read.
   */
  getBrand(brandId: string): Promise<Brand> {
    return request<Brand>(`/brands/${brandId}`)
  },

  /**
   * The calling user's own favourite brands, ordered by
   * `favorite_brands.created_at`. Backs the sidebar Favorites group's data
   * (wiring lands in Phase 11).
   */
  getFavoriteBrands(params?: BrandListParams): Promise<PaginatedResponse<Brand>> {
    return request<PaginatedResponse<Brand>>(
      `/brands/favorites${buildQuery({ page: params?.page, pageSize: params?.pageSize })}`,
    )
  },

  /**
   * Add a brand to the calling user's favourites. Idempotent — favouriting an
   * already-favourited brand is a no-op success. Returns 204 with no body.
   */
  async favoriteBrand(brandId: string): Promise<void> {
    await request<undefined>(`/brands/${brandId}/favorite`, { method: 'PUT' })
  },

  /**
   * Remove a brand from the calling user's favourites. Returns 204 with no
   * body.
   */
  async unfavoriteBrand(brandId: string): Promise<void> {
    await request<undefined>(`/brands/${brandId}/favorite`, { method: 'DELETE' })
  },

  // ── Campaign list — brand-scoped, with status aggregation (Phase 03) ─────

  /**
   * List campaigns with the Phase 00B/03 brand scoping + status-aggregation
   * fields (`brandId`, `masterCount`, `dimensionCount`, `inProgressCount`,
   * `creator`, `lastModifiedAt`), computed server-side in one query (no
   * N+1). `brandId` is optional — omitted preserves the unscoped list, for
   * the coexistence-flag rollout (D-CUTOVER-1).
   *
   * A SEPARATE, additive method: the shipped campaign list still calls
   * `apiClient.get<{ data: Campaign[] }>('/campaigns?pageSize=100')` directly
   * today and is unaffected by this — wiring the extended list into that page
   * belongs to Phase 11.
   */
  getCampaigns(params?: CampaignListParams): Promise<PaginatedResponse<CampaignListItem>> {
    return request<PaginatedResponse<CampaignListItem>>(
      `/campaigns${buildQuery({
        page: params?.page,
        pageSize: params?.pageSize,
        status: params?.status,
        brandId: params?.brandId,
      })}`,
    )
  },
}
